import { Loader2, CheckCircle2, AlertCircle, ShieldAlert, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { VersionGenerationJob } from '@/api/agentVersionGeneration';

interface AgentVersionGenerationPopupsProps {
  jobs: VersionGenerationJob[];
  onView: (job: VersionGenerationJob) => void;
  onDismiss: (jobId: string) => void;
}

/**
 * Bottom-right stack of progress popups for async "Save Changes" generation,
 * styled like the app's toasts. Each popup stays in its loading state until the
 * backend job resolves. Ready/blocked popups are clickable to open the new
 * version's prompt.
 */
export function AgentVersionGenerationPopups({ jobs, onView, onDismiss }: AgentVersionGenerationPopupsProps) {
  if (jobs.length === 0) return null;

  return (
    <div
      data-testid="agent-version-generation-popups"
      className="fixed bottom-4 right-4 z-[110] flex flex-col gap-2 w-[360px] max-w-[calc(100vw-2rem)]"
    >
      {jobs.map((job) => (
        <GenerationPopup key={job.id} job={job} onView={onView} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function GenerationPopup({
  job,
  onView,
  onDismiss,
}: {
  job: VersionGenerationJob;
  onView: (job: VersionGenerationJob) => void;
  onDismiss: (jobId: string) => void;
}) {
  const isLoading = job.status === 'pending' || job.status === 'generating';
  const isReady = job.status === 'ready';
  const isBlocked = job.status === 'blocked';
  const isFailed = job.status === 'failed';
  const clickable = isReady || isBlocked;

  const name = job.agent_name || 'Teammate';
  const versionLabel = job.version_label ? `v${job.version_label}` : 'new version';

  const isDraft = Boolean(job.save_as_draft);

  const title = isLoading
    ? isDraft
      ? 'Generating draft version'
      : 'Generating new version'
    : isReady
      ? isDraft
        ? 'Draft saved'
        : 'New version saved'
      : isBlocked
        ? 'Flagged — not saved'
        : 'Generation failed';

  const description = isLoading
    ? `Compiling ${name}'s configuration into a system prompt…`
    : isReady
      ? isDraft
        ? `${name} ${versionLabel} was saved as a draft. The active version is unchanged. Click to view the prompt.`
        : `${name} ${versionLabel} is now active. Click to view the prompt.`
      : isBlocked
        ? `${name}'s new version was flagged by compliance and was not saved. Click to review.`
        : job.error || 'Something went wrong while generating the prompt.';

  return (
    <div
      data-testid="agent-version-generation-popup"
      data-status={job.status}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onView(job) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onView(job);
              }
            }
          : undefined
      }
      className={cn(
        'relative rounded-xl border shadow-lg bg-white dark:bg-gray-900 px-4 py-3 flex items-start gap-3 transition-colors',
        clickable && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/70',
        isBlocked
          ? 'border-amber-300 dark:border-amber-800'
          : isFailed
            ? 'border-red-300 dark:border-red-800'
            : 'border-gray-200 dark:border-gray-700'
      )}
    >
      <div className="mt-0.5 flex-shrink-0">
        {isLoading && (
          <div className="relative w-5 h-5 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-primary animate-spin" />
          </div>
        )}
        {isReady && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
        {isBlocked && <ShieldAlert className="w-5 h-5 text-amber-500" />}
        {isFailed && <AlertCircle className="w-5 h-5 text-red-500" />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          {isLoading && <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</p>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{description}</p>
      </div>

      {/* Loading popups cannot be dismissed (generation is in flight). */}
      {!isLoading && (
        <button
          aria-label="Dismiss"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(job.id);
          }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
